import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AudienceRole } from "./useCurriculumEligibility";

// Fetch audiences for a specific curriculum
export function useCurriculumAudienceSettings(curriculumId: string) {
  return useQuery({
    queryKey: ["curriculum-audience-settings", curriculumId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curriculum_audiences")
        .select("audience_role")
        .eq("curriculum_id", curriculumId);

      if (error) throw error;
      return (data || []).map((a) => a.audience_role as AudienceRole);
    },
    enabled: !!curriculumId,
  });
}

// Update audiences for a curriculum
export function useUpdateCurriculumAudiences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      curriculumId,
      audiences,
    }: {
      curriculumId: string;
      audiences: AudienceRole[];
    }) => {
      // Delete existing audiences
      const { error: deleteError } = await supabase
        .from("curriculum_audiences")
        .delete()
        .eq("curriculum_id", curriculumId);

      if (deleteError) throw deleteError;

      // Insert new audiences
      if (audiences.length > 0) {
        const inserts = audiences.map((role) => ({
          curriculum_id: curriculumId,
          audience_role: role,
        }));

        const { error: insertError } = await supabase
          .from("curriculum_audiences")
          .insert(inserts);

        if (insertError) throw insertError;
      }

      return { curriculumId, audiences };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["curriculum-audience-settings", data.curriculumId] });
      queryClient.invalidateQueries({ queryKey: ["curriculum-audiences"] });
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
      queryClient.invalidateQueries({ queryKey: ["admin-curricula-visibility"] });
      queryClient.invalidateQueries({ queryKey: ["featured-curricula-landing"] });
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
    },
  });
}
